import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { VocalLabsWebCallService } from 'src/@core/services/vocallabs/vocallabs-web-call.service';

type ReadyState = 'disconnected' | 'connecting' | 'connected';

interface TranscriptItem {
  ts: number;
  speaker?: string;
  text: string;
  final: boolean;
}

/**
 * Global support widget. Uses in-app `VocalLabsWebCallService` (same WS/audio protocol as
 * `@vocallabs/web-sdk`) so Angular 8 + Webpack 4 can compile without transpiling `node_modules`.
 */
@Component({
  selector: 'hour4u-admin-vocallabs-support-widget',
  templateUrl: './vocallabs-support-widget.component.html',
  styleUrls: ['./vocallabs-support-widget.component.scss']
})
export class VocallabsSupportWidgetComponent implements OnInit, OnDestroy {
  isOpen = false;
  state: ReadyState = 'disconnected';
  muted = false;
  transcripts: TranscriptItem[] = [];

  private wsUrl = '';

  get hasWsUrl(): boolean {
    return !!(this.wsUrl && this.wsUrl.trim());
  }

  get stateLabel(): string {
    if (this.state === 'connecting') return 'Connecting';
    return this.state;
  }

  constructor(
    private zone: NgZone,
    private vocalLabs: VocalLabsWebCallService
  ) { }

  ngOnInit(): void {
    this.wsUrl = (environment as any).vocallabsWsUrl || '';
    this.vocalLabs.onConnected(() => {
      this.zone.run(() => {
        this.state = 'connected';
        this.pushSystemTranscript('Connected');
      });
    });
    this.vocalLabs.onDisconnected(() => {
      this.zone.run(() => {
        this.state = 'disconnected';
        this.muted = this.vocalLabs.isMuted();
        this.pushSystemTranscript('Disconnected');
      });
    });
    this.vocalLabs.onError((err) => {
      this.zone.run(() => {
        this.state = 'disconnected';
        this.pushSystemTranscript(this.formatError(err));
      });
    });
    this.vocalLabs.onTranscript((payload) => {
      this.zone.run(() => {
        this.transcripts.unshift({
          ts: Date.now(),
          speaker: payload.speaker,
          text: payload.transcript,
          final: payload.is_final,
        });
        if (this.transcripts.length > 30) {
          this.transcripts = this.transcripts.slice(0, 30);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.vocalLabs.disconnect().catch(() => { /* ignore */ });
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  async connectOrDisconnect(): Promise<void> {
    if (!this.hasWsUrl) {
      return;
    }

    try {
      if (this.state === 'connected' || this.vocalLabs.isConnected()) {
        await this.vocalLabs.disconnect();
        return;
      }
      this.state = 'connecting';
      await this.vocalLabs.connect(this.wsUrl);
    } catch (e) {
      this.zone.run(() => {
        this.state = 'disconnected';
        this.pushSystemTranscript(this.formatError(e));
      });
    }
  }

  toggleMute(): void {
    if (this.state !== 'connected') return;
    this.muted = this.vocalLabs.toggleMute();
  }

  clearTranscripts(): void {
    this.transcripts = [];
  }

  private pushSystemTranscript(text: string): void {
    this.transcripts.unshift({
      ts: Date.now(),
      speaker: 'system',
      text,
      final: true,
    });
    if (this.transcripts.length > 30) {
      this.transcripts = this.transcripts.slice(0, 30);
    }
  }

  private formatError(err: any): string {
    try {
      if (!err) return 'Unknown error';
      if (typeof err === 'string') return err;
      const code = err.code ? String(err.code) : '';
      const msg = err.message ? String(err.message) : JSON.stringify(err);
      return code ? `${code}: ${msg}` : msg;
    } catch (e) {
      return 'Unknown error';
    }
  }
}
